<template>
  <v-sheet
    :class="{ 'is-pending': isPending }"
    class="resourceForm"
  >
    <editing-toolbar
      v-if="!newResource"
      :title="title"
      :no-edit="noEdit"
      :is-editing.sync="isEditingForm"
      :is-pending="isPending"
      :on-start-editing="startEditing"
      :on-stop-editing="stopEditing"
      :on-save="save"
      :save-is-disabled="!formValid"
    />
    <div
      :class="{ 'small-column': columnWidth < 200 }"
      class="pa-3"
    >
      <v-form ref="form">
        <div
          v-for="field in fields"
          :key="fieldKey(field)"
        >
          <div
            v-if="field.subtitle && !isHiding(field.hide)"
            class="v-toolbar__title subtitle"
          >
            <div>{{ field.subtitle }}</div>
          </div>
          <v-row v-else-if="Array.isArray(field)">
            <v-col
              v-for="subfield in field"
              :key="subfield.fieldName"
              :cols="subfield.width ? subfield.width : undefined"
            >
              <v-expand-transition>
                <component
                  v-bind:is="subfield.type + 'Field'"
                  :field="subfield"
                  v-if="!isHiding(subfield.hide)"
                  :to-edit.sync="toEdit[subfield.fieldName]"
                  :rules="getRules(subfield.rules)"
                  :disabled="!isEditingForm || noEdit"
                  :read-only="subfield.readOnly"
                  :items="subfield.items"
                  :watch="subfield.watch"
                  :watched-value="toEdit[subfield.watch]"
                  :item-value="subfield.itemValue"
                  :item-text="subfield.itemText"
                  :type="subfield.inputType"
                  :label="getLabel(subfield)"
                />
              </v-expand-transition>
            </v-col>
          </v-row>
          <div v-else>
            <v-expand-transition>
              <component
                v-bind:is="field.type + 'Field'"
                :field="field"
                v-if="!isHiding(field.hide)"
                :to-edit.sync="toEdit[field.fieldName]"
                :rules="getRules(field.rules)"
                :disabled="!isEditingForm || noEdit"
                :read-only="field.readOnly"
                :items="field.items"
                :watch="field.watch"
                :watched-value="toEdit[field.watch]"
                :item-value="field.itemValue"
                :item-text="field.itemText"
                :type="field.inputType"
                :label="getLabel(field)"
              />
            </v-expand-transition>
          </div>
        </div>
      </v-form>
    </div>
    <v-fade-transition>
      <div
        v-if="isPending"
        class="form-pending"
      >
        <v-progress-circular
          v-if="!newResource"
          indeterminate
        />
      </div>
    </v-fade-transition>
  </v-sheet>
</template>

<script>
import Vue from 'vue'
import { VSheet, VForm } from 'vuetify/lib'
import { toHuman } from '@bit/streamline.web.utils'
import EditingToolbar from '@bit/streamline.web.editing-toolbar'
import getRules from './rules'
import boolean from './boolean'
import color from './color'
import date from './date'
import datetime from './datetime'
import file from './file'
import list from './list'
import svg from './svg'
import text from './text'
import textarea from './textarea'
import info from './info'

export default {
  components: {
    VSheet,
    VForm,
    EditingToolbar,
    svgField: svg,
    booleanField: boolean,
    colorField: color,
    dateField: date,
    fileField: file,
    datetimeField: datetime,
    listField: list,
    textField: text,
    textAreaField: textarea,
    infoField: info
  },
  props: {
    fields: {
      type: Array,
      required: true
    },
    model: {
      type: Object,
      required: true
    },
    onSave: {
      type: Function,
      required: false,
      default: () => { return () => {} }
    },
    title: {
      type: String,
      required: false,
      default: () => ''
    },
    editing: {
      type: Boolean,
      required: false,
      default: false
    },
    noEdit: {
      type: Boolean,
      required: false,
      default: false
    },
    newResource: {
      type: Boolean,
      required: false,
      default: false
    },
    isPending: {
      type: Boolean,
      required: false,
      default: false
    },
    isCompleted: {
      type: Boolean,
      required: false,
      default: false
    },
    disableValidation: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      formValid: true,
      toEdit: {},
      isEditingForm: this.newResource || this.editing,
      columnWidth: null
    }
  },
  watch: {
    toEdit: {
      handler () {
        if (!this.disableValidation) { Vue.nextTick(this.validateForm) }
        this.$emit('edit', this.toEdit)
        if (this.newResource) { this.$emit('update:model', this.toEdit) }
      },
      deep: true
    },
    model (newModel) {
      if (JSON.stringify(newModel) !== JSON.stringify(this.toEdit)) {
        if (!this.newResource) { this.isEditingForm = false }
        this.toEdit = newModel
      }
    },
    editing () {
      this.isEditingForm = this.editing
      this.toEdit = this.setObject(this.model)
    }
  },
  mounted () {
    this.toEdit = this.setObject(this.model)
    this.columnWidth = this.$el.clientWidth
  },
  methods: {
    toHuman,
    getRules,
    validateForm () {
      const validation = this.$refs.form.validate()

      this.formValid = validation
      this.$emit('update:is-completed', validation)
      return validation
    },
    fieldKey (field) {
      if (field.subtitle) { return field.subtitle }
      if (Array.isArray(field) && field[0].fieldName) { return `row-${field[0].fieldName}` }
      if (field.fieldName) { return field.fieldName }
      if (field.itemText) { return field.itemText }
      return JSON.stringify(field)
    },
    getLabel (field) {
      const label = field.label || field.fieldName
      if (!field.rules || field.rules.length === 0) { return this.toHuman(label) }
      return this.toHuman(label) + '*'
    },
    isHiding (key) {
      if (!key) { return false }
      // If multiple values are given, check if any of them are 'true'
      if (key.includes(' ')) {
        return key.split(' ').some(subkey => this.isHiding(subkey))
      }
      // If the key starts with a '!', do a reverse check
      if (key.startsWith('!')) {
        const value = this.toEdit[key.substring(1)]
        return value === false || value === undefined
      }
      return this.toEdit[key] === true
    },
    async save () {
      this.$emit('save', this.toEdit)
      if (this.onSave) {
        await this.onSave(JSON.parse(JSON.stringify(this.toEdit)))
      }
    },
    setObject (obj) {
      if (!obj) { return {} }
      return JSON.parse(JSON.stringify(obj))
    },
    stopEditing () {
      this.$emit('update:editing', this.isEditingForm)
      this.toEdit = this.setObject(this.model)
    },
    startEditing () {
      this.$emit('update:editing', this.isEditingForm)
      this.toEdit = this.setObject(this.model)
    }
  }
}
</script>

<style scoped lang="scss">
  .v-sheet {
    position: relative;

    .pa-3.small-column {
      padding-left: 4px !important;
      padding-right: 4px !important;
    }

    .v-form {
      .v-label {
        height: 25px;
        line-height: 25px;
      }

      .v-label--active {
        font-size: 1.2em;
      }

      .v-input.v-input--is-disabled {
        .v-input__slot::before {
          border: none;
        }

        input {
          color: #363636;
        }
        textarea {
          color: #363636;
        }

        .v-select__selection {
          color: #363636;
        }
      }
    }

    .v-form {
      filter: blur(0px);
      transition: 0.4s filter;

      .subtitle {
        padding: 24px 0 8px 4px;
      }

      .col {
        padding: 0 12px;
      }
    }

    .form-pending,
    .fade-enter-active,
    .fade-leave-active {
      transition: 0.2s opacity;
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .fade-enter,
    .fade-leave-to {
      opacity: 0;
    }
  }

  .v-sheet.is-pending {
    .v-form {
      filter: blur(5px);
    }
  }
</style>
